import { FormProvider } from "react-hook-form";
import { Bookmark } from "lucide-react";
import { FormCheckbox, FormInput, FormSelect, FormDatePicker } from "@/components";
import { Button } from "@/components/ui";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  CURRICULUM_NOTES,
  INCLUDES_3D_PRINTER,
  INCLUDES_CHAIRS,
  INCLUDES_ELECTRICAL,
  INCLUDES_PAINTING,
  INCLUDES_PODIUM_MIC,
  INCLUDES_STORAGE_CABINETS,
  INCLUDES_TABLETS,
  INCLUDES_TEACHERS_TABLE,
  INCLUDES_TV,
  INFRASTRUCTURE_NOTES,
  OTHER_NOTES,
} from "./consts";
import { LOGIN_ROLES } from "@/lib/utils";

interface InventoryDetailsProps {
  inventoryDetailsForm: any;
  requiredFields: string[];
  isLoadingExisting: boolean;
  onSubmitInventoryDetails: (data: any) => void;
}

const InventoryDetails = ({
  inventoryDetailsForm,
  requiredFields,
  isLoadingExisting,
  onSubmitInventoryDetails,
}: InventoryDetailsProps) => {
  const inventoryDetailsErrors = inventoryDetailsForm.formState.errors;

  const loggedInUserData = useSelector(
        (state: RootState) => state.currentData.loggedInUserData
      );
    
      // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
  const roles = loggedInUserData?.role || [];
    
     
  const hasRole = (role: string) => Array.isArray(roles) && roles.includes(role);

  return (
    <FormProvider {...inventoryDetailsForm}>
      <form onSubmit={inventoryDetailsForm.handleSubmit(onSubmitInventoryDetails)} className="relative">
        <div className="bg-white rounded-lg shadow-sm px-6 py-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[18px] font-semibold text-primary">
              Inventory Details
            </h2>
            {((hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN)) &&
            <Button type="submit" variant="default">
              Save
            </Button>
            )}
          </div>

          {/* Inventory checkboxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 mb-6">
            <FormCheckbox
              name={INCLUDES_TV}
              control={inventoryDetailsForm.control}
              label="Includes TV?"
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormCheckbox
              name={INCLUDES_3D_PRINTER}
              control={inventoryDetailsForm.control}
              label="Includes 3D Printer?"
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormCheckbox
              name={INCLUDES_PAINTING}
              control={inventoryDetailsForm.control}
              label="Includes Painting?"
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormCheckbox
              name={INCLUDES_ELECTRICAL}
              control={inventoryDetailsForm.control}
              label="Includes Electrical?"
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormCheckbox
              name={INCLUDES_STORAGE_CABINETS}
              control={inventoryDetailsForm.control}
              label="Includes Storage Cabinets?"
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormCheckbox
              name={INCLUDES_TEACHERS_TABLE}
              control={inventoryDetailsForm.control}
              label="Includes Teachers Table?"
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormCheckbox
              name={INCLUDES_PODIUM_MIC}
              control={inventoryDetailsForm.control}
              label="Includes Podium and Mic?"
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormCheckbox
              name={INCLUDES_TABLETS}
              control={inventoryDetailsForm.control}
              label="Includes Tablets?"
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormCheckbox
              name={INCLUDES_CHAIRS}
              control={inventoryDetailsForm.control}
              label="Includes Chairs?"
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
          </div>

          {/* Notes Section */}
          <div className="grid grid-cols-1 gap-5">
            <FormInput
              name={INFRASTRUCTURE_NOTES}
              control={inventoryDetailsForm.control}
              label="Infrastructure Notes"
              placeholder="Please enter"
              error={inventoryDetailsErrors[INFRASTRUCTURE_NOTES]?.message}
              isRequired={requiredFields.includes(INFRASTRUCTURE_NOTES)}
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormInput
              name={CURRICULUM_NOTES}
              control={inventoryDetailsForm.control}
              label="Curriculum Notes"
              placeholder="Please enter"
              error={inventoryDetailsErrors[CURRICULUM_NOTES]?.message}
              isRequired={requiredFields.includes(CURRICULUM_NOTES)}
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
            <FormInput
              name={OTHER_NOTES}
              control={inventoryDetailsForm.control}
              label="Other Notes"
              placeholder="Please enter"
              error={inventoryDetailsErrors[OTHER_NOTES]?.message}
              isRequired={requiredFields.includes(OTHER_NOTES)}
              disabled={hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? false : true}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default InventoryDetails;